import { Icon20ChevronRightOutline as ChevronRight } from '@vkontakte/icons';
import { Text } from '@vkontakte/vkui';
import classNames from 'clsx';
import SidebarImage from 'img/freemium/sidebarBanner.png';
import SidebarImageDark from 'img/freemium/sidebarBannerDark.png';
import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { freemiumTariffPurchaseID, isHardFreemium, isSoftFreemium } from 'reactApp/appHelpers/featuresHelpers/features/freemium';
import { useFreemiumDaysString, useFreemiumSubscriptionDays } from 'reactApp/hooks/useFreemium';
import { openTariffBuy } from 'reactApp/modules/payment/payment.module';
import { productsController } from 'reactApp/modules/products/products.controller';
import { isCurrentDarkTheme } from 'reactApp/modules/theme/theme.selectors';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { sendDwh } from 'reactApp/utils/ga';

import styles from './FreemiumSidebarBanner.css';

const DISCOUNT_TARIFF_ID = 'W256GB_1m_family_features_discount_3m';

const getAnalyticsName = (): string => {
    if (isHardFreemium) {
        return 'mnz_sub_end_soon_unlimited_hard_freemium';
    }
    if (isSoftFreemium) {
        return 'mnz_sub_end_soon_unlimited_soft_freemium';
    }
    return 'mnz_sub_end_soon_unlimited_soft_freemium';
};

const getPaySource = (isV2: boolean): string => {
    if (isHardFreemium) {
        return 'mnz_family_unlimited_hard_freemium_sidebar_sub_end_soon';
    }
    if (isSoftFreemium) {
        return isV2 ? 'mnz_family_unlimited_soft_freemium_sidebar_sub_end_soon' : 'mnz_family_unlimited_soft_freemium_sidebar';
    }
    return isV2 ? 'mnz_family_unlimited_soft_freemium_sidebar_sub_end_soon' : 'mnz_family_unlimited_soft_freemium_sidebar';
};

export const FreemiumSidebarBanner = memo(() => {
    const dispatch = useDispatch();
    const isDarkTheme = useSelector(isCurrentDarkTheme);
    const freemiumDays = useFreemiumSubscriptionDays();
    const freemiumDaysString: string | undefined = useFreemiumDaysString();

    const isV2 = useMemo(() => freemiumDays <= 3 && freemiumDays > 0, [freemiumDays]);
    const analyticsName = getAnalyticsName();
    const nameBannerV1 = isHardFreemium
        ? 'mnz_family_hard_freemium_subscription_information'
        : 'mnz_family_soft_freemium_subscription_information';
    const nameBannerV2 = analyticsName;

    const onClick = useCallback(async () => {
        sendDwh({
            eventCategory: 'sidebar-banner',
            action: 'click',
            dwhData: { name_banner: nameBannerV1 },
            dwh_enabled: true,
        });
        await productsController.loadProducts();
        dispatch(
            openTariffBuy({
                productId: freemiumTariffPurchaseID,
                paySource: getPaySource(false),
                title: 'Семейный тариф Mail Space',
            })
        );
    }, [dispatch, nameBannerV1]);

    const onButtonClick = useCallback(
        async (e: React.MouseEvent) => {
            e.stopPropagation();
            sendDwh({
                eventCategory: 'sidebar-banner',
                action: 'click',
                dwhData: { name_banner: nameBannerV2 },
                dwh_enabled: true,
            });
            await productsController.loadProducts();
            dispatch(
                openTariffBuy({
                    productId: DISCOUNT_TARIFF_ID,
                    paySource: getPaySource(true),
                    title: 'Семейный тариф Mail Space',
                })
            );
        },
        [dispatch, nameBannerV2]
    );

    if (freemiumDays <= 0) {
        return null;
    }

    return (
        <div className={classNames(styles.root, { [styles.rootV2]: isV2 })} onClick={!isV2 ? onClick : undefined}>
            <img src={isDarkTheme ? SidebarImageDark : SidebarImage} alt="Freemium" className={styles.image} />
            <Text className={styles.title} weight="2">
                Пробная подписка
            </Text>
            <Text className={isV2 ? styles.subtitleV2 : styles.subtitle} weight="2">
                {freemiumDaysString && `Ещё ${freemiumDaysString}`}
            </Text>
            {!isV2 && <ChevronRight className={styles.chevron} />}
            {isV2 && (
                <div className={styles.buttonWrapper}>
                    <Button theme="octavius" primary sizeMode={ButtonSizeMode.small} onClick={onButtonClick} className={styles.button}>
                        Продлить со скидкой
                    </Button>
                </div>
            )}
        </div>
    );
});

FreemiumSidebarBanner.displayName = 'FreemiumSidebarBanner';
